import React, { useEffect, useMemo, useState } from "react";
import moment from 'moment';

import PanelTemplate from "../../templates/Panel";
import Table from "../../components/Table";
import Box from "../../components/Box";
import UserCell from "../../components/UserCell";
import Button from "../../components/Button";
import PageHeader from "../../components/PageHeader";
import FilterSimple from "../../components/FilterSimple";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import axios from "axios";

import { ModalDelete } from "../../components/ModalDelete";
import { getToken, hasPermission } from "../../services/auth";
import Pagination from "../../components/Pagination";
import { getSubdomain } from "../../helpers/domain";
import { ModalCreateCompany } from "../../components/ModalCreateCompany";
import { ModalImportCompanyClients } from "../../components/ModalImportCompanyClients";
import SortHeader from "../../components/SortHeader";

const indexMap = [
    { index: 'Código', value: 'code' },
    { index: 'Nome', value: 'trade_name' },
    { index: 'CNPJ', value: 'cnpj' },
    { index: 'Cadastrado em', value: 'created_at' },
    { index: 'Atualizado em', value: 'updated_at' },
];

const companySearchParams = [
    {name: 'Nome da empresa', key: '[trade_name]', operation: 'contain', type: 'text' },
    {name: 'Razão social', key: '[company_name]', operation: 'contain', type: 'text' },
    {name: 'CNPJ', key: '[cnpj]', operation: 'contain', mask: 'convertToCnpj', type: 'text' },
    {name: 'Código', key: '[code]', operation: 'contain', type: 'text' },
    {name: 'Onde nos conheceu', key: '[origin]', operation: 'contain', type: 'text'}
];

const getCompanySearchParam = field => (
    companySearchParams.find(param => param.key === field) || companySearchParams[0]
);

const normalizePage = page => {
    const parsedPage = parseInt(page, 10);
    return Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;
};

const parseCompanyListFilters = search => {
    const params = new URLSearchParams(search);
    const searchParam = getCompanySearchParam(params.get('search_field'));

    return {
        page: normalizePage(params.get('page')),
        searchField: searchParam.key,
        searchOperation: params.get('search_operation') || searchParam.operation || 'contain',
        search: params.get('search') || '',
        ordenationItem: params.get('sort') || 'trade_name',
        orientation: params.get('orientation') || 'asc',
    };
};

const buildCompanyApiQuery = filters => {
    const params = new URLSearchParams();

    if (filters.search.trim() !== '') {
        params.append(`filters[0]${filters.searchField}[${filters.searchOperation}]`, filters.search.trim());
    }

    params.set('ordenation_item', filters.ordenationItem);
    params.set('orientation', filters.orientation);
    params.set('page', filters.page);

    return params.toString();
};

const buildCompanyListSearch = filters => {
    const params = new URLSearchParams();

    if (filters.page > 1) params.set('page', filters.page);
    if (filters.search.trim() !== '') {
        params.set('search_field', filters.searchField);
        params.set('search_operation', filters.searchOperation);
        params.set('search', filters.search.trim());
    }
    if (filters.ordenationItem !== 'trade_name') params.set('sort', filters.ordenationItem);
    if (filters.orientation !== 'asc') params.set('orientation', filters.orientation);

    return params.toString();
};

export const CompanyList = () => {
    const [loading, setLoading] = useState(false);

    const [companies, setCompanies] = useState([]);
    const [data, setData] = useState({header: [], rows: []});

    const [showCreateCompany, setShowCreateCompany] = useState(false);

    const [showImportClients, setShowImportClients] = useState(false);

    const [companyToDelete, setCompanyToDelete] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showConfirmMultipleDelete, setShowConfirmMultipleDelete] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const companyFilters = useMemo(() => parseCompanyListFilters(location.search), [location.search]);

    const updateCompanyListUrl = (changes, resetPage = true) => {
        const nextFilters = {
            ...companyFilters,
            ...changes,
            page: resetPage ? 1 : (changes.page || companyFilters.page),
        };
        const search = buildCompanyListSearch(nextFilters);

        navigate({
            pathname: location.pathname,
            search: search ? `?${search}` : '',
        });
    }

    const handleOrdenation = (orientationItem, oredenation) => {
        updateCompanyListUrl({
            ordenationItem: orientationItem,
            orientation: oredenation,
        });
    };

    useEffect(() => {
        getCompanies();
    }, [location.search]);

    useEffect(() => {
        generateData();
    }, [companies, location.search]);

    const generateData = () => {
        let header = ['Código', 'Nome', 'CNPJ', 'Onde conheceu', 'Responsável', 'Cadastrado em', 'Atualizado em'];
        const rows = [];

        if(hasPermission('delete_company')){
            header.push('');
        }

        companies.map(company => {
            let buttons = [];
            let canEdit = true;
            const returnTo = encodeURIComponent(`/company/list${location.search}`);

            if(company.type.includes('billing') && !hasPermission('company_billing')){
                canEdit = false;
            }
            if(company.type.includes('product') && !hasPermission('company_supplier')){
                canEdit = false;
            }
            if(company.type.includes('customization') && !hasPermission('company_supplier')){
                canEdit = false;
            }

            let canDelete = hasPermission('delete_company') && canEdit && company.company_integration === null;

            if(canDelete){
                buttons.push(
                    <Button type={`delete`} svg={`delete`} action={() => setCompanyToDelete(company.id)} />
                );
            }

            rows.push({
                data: [
                    company.code,
                    company.trade_name,
                    company.cnpj,
                    company.origin,
                    company?.admin ? <UserCell name={company.admin.name} photo={company.admin.photo_url} /> : '',
                    company.created_at ? moment(company.created_at).format('DD/MM/YYYY HH:mm') : '',
                    company.created_at ? moment(company.updated_at).format('DD/MM/YYYY HH:mm') : ''
                ],
                buttons,
                to: canEdit ? `/company/edit/${company.id}?return_to=${returnTo}` : '#',
                id: canDelete ? company.id : null,
                disable: !canEdit
            })
        });

        setData({header, rows});
    }

    const getCompanies = () => {
        setCompanies([]);
        setLoading(true);

        if (typeof cancelToken != typeof undefined) {
            window.cancelToken.cancel()
        }

        window.cancelToken = axios.CancelToken.source();

        api.get(`/company/paginate?${buildCompanyApiQuery(companyFilters)}`, {
            cancelToken: window.cancelToken.token
        }).then(res => {
            setCompanies(res.data.resources.data);
            setCurrentPage(res.data.resources.pagination.current_page);
            setLastPage(res.data.resources.pagination.last_page)
            setPerPage(res.data.resources.pagination.per_page)
            setTotalResults(res.data.resources.pagination.total)
            setHasNextPage(res.data.resources.pagination.next_page_url !== null);
            setHasPrevPage(res.data.resources.pagination.prev_page_url !== null);
        }).catch(error => {
            
        }).then(() => setLoading(false));
    }

    const deleteCompany = () => {
        setLoading(true);

        api.delete(`/company/${companyToDelete}`).then(res => {
            setCompanyToDelete(null);
            getCompanies();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const deleteMultipleCompanies = () => {
        setLoading(true);

        api.delete(`/company/multiple`, {
            data: {
                ids: selectedIds
            }
        }).then(res => {
            setSelectedIds([]);
            getCompanies();
            setShowConfirmMultipleDelete(false);
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const handleSetFilter = (value, metadata) => {
        updateCompanyListUrl({
            searchField: metadata?.field || companyFilters.searchField,
            searchOperation: metadata?.operation || companyFilters.searchOperation,
            search: metadata?.value ?? '',
        });
    }

    const handleSetPage = page => {
        updateCompanyListUrl({ page }, false);
    }

    const exportCompanies = () => {
        window.open(`${process.env.REACT_APP_URL}/company/export?_token=${getToken()}&_subdomain=${getSubdomain()}`);
    }

    const refresh = () => {
        if(location.search === ''){
            getCompanies();
        } else {
            navigate(location.pathname);
        }
    }

    return (
        <>
            <ModalDelete
                show={showConfirmMultipleDelete}
                text={<p>Tem certeza que deseja apagar <strong>{selectedIds.length} {selectedIds.length > 1 ? 'empresas' : 'empresa'}</strong>?</p>}
                action={deleteMultipleCompanies}
                loading={loading}
                close={() => setShowConfirmMultipleDelete(false)}
            />
            <ModalDelete
                show={companyToDelete !== null}
                action={deleteCompany}
                loading={loading}
                close={() => setCompanyToDelete(null)}
            />
            <ModalCreateCompany
                show={showCreateCompany}
                close={() => setShowCreateCompany(false)}
                companyStored={company => navigate(`/company/edit/${company.id}`)}
            />
            <ModalImportCompanyClients
                show={showImportClients}
                close={() => setShowImportClients(false)}
                companyStored={company => navigate(`/company/edit/${company.id}`)}
                refresh={refresh}
            />
            <PanelTemplate id="company-list">
                <PageHeader
                    left={
                        <FilterSimple
                            params={companySearchParams}
                            filter={handleSetFilter}
                            externalValue={companyFilters.search}
                            externalOption={companyFilters.searchField}
                        />
                    }

                    right={
                        <>
                            {hasPermission('delete_company') && selectedIds.length > 0 &&
                                <Button 
                                type={`secondary`} 
                                size={`small`} 
                                svg={`delete`}
                                text={`Excluir ${selectedIds.length} selecionados`} 
                                action={() => setShowConfirmMultipleDelete(true)}  
                                />
                            }
                            {hasPermission('export_company') &&
                                <Button type={`secondary`} size={`small`} text={`Importar empresas`} action={() => setShowImportClients(true)} />
                            }
                            {hasPermission('export_company') &&
                                <Button type={`secondary`} size={`small`} text={`Exportar empresas`} action={exportCompanies}  />
                            }
                            {hasPermission('create_company') &&
                                <Button type={`primary`} size={`small`} text={`Cadastrar empresa`} svg="plus-white" action={() => setShowCreateCompany(true)}  />
                            }
                        </>
                    }
                />
                
                <Box className={`no-padding with-pagination`}>
                    <Table 
                        header={data.header} 
                        rows={data.rows} 
                        loading={loading} 
                        checks={true} 
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                        sortOrdenationAction={(index) => (
                        <SortHeader
                            index={index}
                            ordenationItem={companyFilters.ordenationItem}
                            orientation={companyFilters.orientation}
                            onSort={handleOrdenation}
                            indexMap={indexMap}
                            />
                        )}
                    />
                </Box>
                {!loading &&
                    <Pagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        perPage={perPage}
                        totalResults={totalResults}
                        hasNextPage={hasNextPage}
                        hasPrevPage={hasPrevPage}
                        setCurrentPage={handleSetPage}
                    />
                }
            </PanelTemplate>
        </>
    );
}

export default CompanyList;